/**
 * Helper defensivo para resolver `folder.reference` en cualquier formato.
 *
 * En el schema clásico de DAYSI, cada entrada de `folders[]` tiene un
 * `reference: { path, id }` donde:
 *   - `path` es el DocumentReference padre del folder hijo
 *   - `id` es el ID de la sub-colección que contiene al folder hijo
 *
 * Cuando los datos se crean desde la UI (cliente SDK), `path` queda como
 * DocumentReference REAL (objeto con métodos `.collection()`, `.doc()`).
 *
 * Cuando los datos se crean desde un script (admin SDK), `path` queda como
 * STRING (porque el admin SDK no permite serializar DocumentReference como
 * valor de campo). Mismo formato que usa `tools.js resolveFolderByNamePath`
 * server-side, ya defensivo desde antes.
 *
 * Las funciones de abajo unifican ambos formatos para que la UI no rompa
 * si los datos se sembraron desde admin (ej. módulo Inbound, importaciones
 * masivas, scripts de migración).
 *
 * Uso típico:
 *   import { getChildCollection } from './folder_ref';
 *   const collRef = getChildCollection(firebase, folder.reference);
 *   if (!collRef) { ... handle bad data ... }
 *   loadFolders(collRef);
 */

/**
 * Devuelve la CollectionReference que contiene los descendientes inmediatos
 * de la entrada `folder`. Equivale a `path.collection(id)` cuando path es
 * DocumentReference, o a construirlo desde el string path.
 *
 * @param {function|object} firebase  El objeto firebase de useFirebase()
 *                                    o el factory que llega a thunks redux.
 *                                    El helper resuelve ambos.
 * @param {object} reference          `{ path, id }` — path puede ser
 *                                    DocumentReference o string FQDN.
 * @returns {CollectionReference|null}
 */
const getFirestore = (firebase) => {
  if (!firebase) return null;
  if (typeof firebase === 'function') {
    return firebase().firestore();
  }
  if (typeof firebase.firestore === 'function') {
    return firebase.firestore();
  }
  return null;
};

export const getChildCollection = (firebase, reference) => {
  if (!reference?.id) return null;
  const p = reference.path;
  // Caso 1: path es DocumentReference (cliente SDK normal)
  if (p && typeof p.collection === 'function') {
    return p.collection(reference.id);
  }
  // Caso 2: path es string (sembrado vía admin SDK)
  if (typeof p === 'string') {
    try {
      const db = getFirestore(firebase);
      return db ? db.doc(p).collection(reference.id) : null;
    } catch {
      return null;
    }
  }
  // Caso 3: path es objeto con `.path` (CollectionReference admin serializada)
  if (p && typeof p.path === 'string') {
    try {
      const db = getFirestore(firebase);
      return db ? db.doc(p.path).collection(reference.id) : null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Devuelve el DocumentReference del folder hijo apuntado por `reference`.
 * Equivale a `path.doc(id)` o construirlo desde string.
 */
export const getChildDoc = (firebase, reference) => {
  if (!reference?.id) return null;
  const p = reference.path;
  if (p && typeof p.doc === 'function') {
    return p.doc(reference.id);
  }
  if (typeof p === 'string') {
    try {
      // path es el doc padre → doc(id) bajo él daría una sub-collection.
      // Para get del doc HIJO real, el path completo es {p}/{id}? Solo si
      // path es CollectionReference. Si path es DocumentReference, el id
      // es de una sub-collection no de un doc.
      //
      // En el schema DAYSI, reference apunta a un doc dentro de una
      // sub-collection que vive en el padre (path). Por lo tanto el
      // doc del folder hijo NO está en {p}/{id} sino en
      // {p}/{some-coll}/{id}. No tenemos suficiente info aquí para
      // resolver. Quien necesite el doc debe usar getChildCollection
      // y luego .doc() con un id válido del array `folders[]`.
      return null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Devuelve un DocumentReference para `tokens` u otra collection global
 * usando solo firebase, dado un path string.
 */
export const docFromPath = (firebase, path) => {
  if (!path || typeof path !== 'string') return null;
  try {
    return firebase().firestore().doc(path);
  } catch {
    return null;
  }
};
